import { AgentTriggerIcon } from '@/components/icons'
import { helpTopicUrl } from '@/lib/help-links'
import type { BlockConfig, OutputFieldDefinition } from '@/blocks/types'

/**
 * The brand events Analytics announces, by the name the server's
 * `deployments::events` listens for. The Run with Agent button on the gap and
 * opportunities screens starts the same run with the same payload.
 */
export const ONDIT_EVENTS = [
  { id: 'gap_found', label: 'Gap Found' },
  { id: 'opportunities_ready', label: 'Opportunities Ready' },
  { id: 'draft_ready', label: 'Draft Ready' },
  { id: 'page_published', label: 'Page Published' },
  { id: 'reflow_finished', label: 'Reflow Finished' },
] as const

export type OnditEvent = (typeof ONDIT_EVENTS)[number]['id']

const on = (value: OnditEvent | OnditEvent[]) => ({ field: 'eventType', value })
const field = (
  type: 'string' | 'number' | 'boolean' | 'json',
  description: string,
  when: OnditEvent | OnditEvent[]
): OutputFieldDefinition => ({ type, description, condition: on(when) })
const PAGE: OnditEvent[] = ['draft_ready', 'page_published']

export const OnditEventBlock: BlockConfig = {
  type: 'ondit_event',
  triggerAllowed: true,
  name: 'Ondit Event',
  description: 'Run this workflow when Analytics finds something about the brand',
  longDescription:
    'Starts a deployed workflow when a gap is found, an opportunities report is ready, a page is drafted or published, or a reflow reading finishes.',
  bestPractices: `
  - Pick one event. The workflow must be deployed; runs start within a minute of the event.
  - Feed the ids in the payload (gapId, pageId, reportId) to Ondit blocks.
  - Runs use the credits of the person who deployed the workflow, up to its deployment credit limit.
  `,
  category: 'triggers',
  bgColor: '#33C482',
  icon: AgentTriggerIcon,
  docsLink: helpTopicUrl('workflows'),
  canvasPresentation: {
    defaultTitle: 'Ondit Event',
    triggerSentences: { default: ['Run on', { field: 'eventType', core: true }] },
  },
  subBlocks: [
    {
      id: 'eventType',
      title: 'Event',
      type: 'dropdown',
      options: ONDIT_EVENTS.map((e) => ({ id: e.id, label: e.label })),
      value: () => 'gap_found',
      required: true,
      mode: 'trigger',
    },
  ],
  tools: { access: [] },
  inputs: {},
  outputs: {
    event: { type: 'string', description: 'The event that started this run' },
    brandId: { type: 'string', description: 'The brand the event is about' },
    timestamp: { type: 'string', description: 'When the event happened' },
    gapId: field('string', 'The gap', ['gap_found', ...PAGE]),
    question: field('string', 'The question rivals were cited on', 'gap_found'),
    questionId: field('string', 'The question id', 'gap_found'),
    engine: field('string', 'The answer engine', 'gap_found'),
    week: field('string', 'The week the gap was measured', 'gap_found'),
    suggestedKind: field('string', 'The kind of page suggested', 'gap_found'),
    competitorDomains: field('json', 'Domains cited instead', 'gap_found'),
    reportId: field('string', 'The opportunities report', 'opportunities_ready'),
    summary: field('json', 'The report summary', 'opportunities_ready'),
    opportunities: field('json', 'Each finding: category, title, why', 'opportunities_ready'),
    pageId: field('string', 'The page', [...PAGE, 'reflow_finished']),
    title: field('string', 'The page title', PAGE),
    description: field('string', 'The page description', PAGE),
    url: field('string', 'The page address on Ondit', PAGE),
    slug: field('string', 'The page slug', PAGE),
    status: field('string', 'The status now', [...PAGE, 'gap_found']),
    locale: field('string', 'The language', [...PAGE, 'gap_found', 'opportunities_ready']),
    kind: field('string', 'The page kind', PAGE),
    reflowId: field('string', 'The reflow reading', 'reflow_finished'),
    pageTitle: field('string', 'The page title', 'reflow_finished'),
    beforeWeek: field('string', 'The week measured before', 'reflow_finished'),
    afterWeek: field('string', 'The week measured after', 'reflow_finished'),
    before: field('number', 'Citation share before', 'reflow_finished'),
    after: field('number', 'Citation share after', 'reflow_finished'),
    delta: field('number', 'Change in citation share', 'reflow_finished'),
    significant: field('boolean', 'Whether the change is significant', 'reflow_finished'),
  },
}
